import React from "react";
import { styled } from "@linaria/react";
import Menu from "@ui/Menu";
import { useModals } from "@ui/lib/hooks/useModal";
import Option, { OptionGroup } from "@ui/Option";
import Modal from "@ui/Modal";
import DeleteProjectModalForm from "../../Projects/DeleteProjectModalForm";
import { fonts } from "@ui";
import useToasts from "@ui/Toast/useToasts";
import t from "../../../i18n";
import { HIDE_ON_EXPORT } from "@ui/lib/downloadPage";
import { usePost, usePut } from "../../../api/useCall";
import CreateUpdateProjectsModalForm from "../../Projects/CreateUpdateProjectsModalForm";
import type { Project } from "../../../api/useProjects";
import { useCanUserDoOnProject } from "../../canUserDo";
import ButtonIconV2 from "@ui/ButtonIconV2";
import {
  AddCircleLine,
  DeleteBinLine,
  EarthLine,
  FileCopyLine,
  FlaskFill,
  MoreFill,
  PencilLine,
  RefreshFill,
  RefreshLine,
  RestartLine,
} from "@ui/IconV2/react";
import DuplicateProjectModalForm from "../../Projects/DuplicateProjectModalForm";
import { MTS_ENV } from "../../../lib/setupEnv";
import LaunchAnalysisSelectionModalForm from "../NavbarActions/LaunchAnalysisSelectionAction/LaunchAnalysisSelectionModalForm";

const StyledMenu = styled(Menu)`
  font: ${fonts.body2};
`;

interface P {
  project: Project;
}

function HeaderDropdownMenu({ project }: P) {
  const { addToast } = useToasts();
  const { modal, closeModal, openModal } = useModals<
    "edit" | "delete" | "reset_analyses" | "duplicate"
  >();
  const openEditModal = React.useCallback(() => openModal("edit"), [openModal]);
  const openDeleteModal = React.useCallback(() => openModal("delete"), [openModal]);
  const openDuplicateModal = React.useCallback(() => openModal("duplicate"), [openModal]);
  const openResetAnalysesModal = React.useCallback(() => openModal("reset_analyses"), [openModal]);

  const updateProject = usePut("/projects/:projectId(uuid)");

  const togglePublishProject = React.useCallback(async () => {
    await updateProject({
      body: {
        ...project,
        draft: !project.draft,
      },
      routeParams: { projectId: project.id },
    });

    addToast(
      "success",
      project.draft
        ? t("project.header.toast.promoted_production")
        : t("project.header.toast.relegated_to_research"),
    );
    return true;
  }, [addToast, project, updateProject]);

  const isProdProject = !project.draft;

  const canUserEditProject = useCanUserDoOnProject("PROJECT_EDIT", project.id);
  const canResetProject = useCanUserDoOnProject("PROJECT_SETUP", project.id);
  const canManageProject = useCanUserDoOnProject("PROJECT_MANAGE", project.id);
  const canDoAdminTasks =
    useCanUserDoOnProject("ADMIN_SETTING_MANAGE", project.id) || MTS_ENV.INTEGRATION;
  const canDuplicateProject = useCanUserDoOnProject("PROJECT_DUPLICATE", project.id);

  const launchCac = usePost("/projects/:projectId(uuid)/cac/launch");
  const dispatchCac = usePost("/projects/:projectId(uuid)/cac/dispatch");
  const resetCac = usePost("/projects/:projectId(uuid)/cac/reset");
  const createMissingAnalysesCac = usePost(
    "/projects/:projectId(uuid)/cac/create-missing-analyses",
  );

  if (
    [canUserEditProject, canManageProject, canResetProject].every(
      permission => permission === false,
    )
  )
    return null;

  return (
    <>
      <Modal
        title={t("projects.modal.update_project")}
        isOpen={modal === "edit"}
        onRequestClose={closeModal}
      >
        <CreateUpdateProjectsModalForm
          onRequestClose={closeModal}
          projectIds={[project.id]}
          okButtonName={t("modalForm.button.name.edit")}
        />
      </Modal>
      <Modal
        title={t("projects.modal.delete.delete_project")}
        isOpen={modal === "delete"}
        onRequestClose={closeModal}
      >
        <DeleteProjectModalForm project={project} onRequestClose={closeModal} />
      </Modal>
      <Modal
        title={t("projects.modal.title.duplicate_project")}
        isOpen={modal === "duplicate"}
        onRequestClose={closeModal}
      >
        <DuplicateProjectModalForm project={project} onRequestClose={closeModal} />
      </Modal>
      <Modal
        title={t("projects.modal.restart_analysis_selection.title")}
        subtitle={t("projects.modal.restart_analysis_selection.subtitle")}
        isOpen={modal === "reset_analyses"}
        onRequestClose={closeModal}
        size="xl"
      >
        <LaunchAnalysisSelectionModalForm
          modalType="RESTART_ANALYSIS_SELECTION"
          project={project}
          onRequestClose={closeModal}
        />
      </Modal>
      <StyledMenu
        arrowWidth={0}
        // gap={16}
        placement="bottom"
        overlay={
          <>
            <OptionGroup>
              {canUserEditProject && (
                <Option closeOnClick onClick={openEditModal}>
                  <PencilLine />
                  {t("project.header.dropdown.edit_project")}
                </Option>
              )}
              {canUserEditProject && (
                <Option closeOnClick onClick={togglePublishProject}>
                  {project.draft ? (
                    <>
                      <EarthLine />
                      {t("project.header.dropdown.promote_production")}
                    </>
                  ) : (
                    <>
                      <FlaskFill />
                      {t("project.header.dropdown.relegate_to_research")}
                    </>
                  )}
                </Option>
              )}
              {canDuplicateProject && (
                <Option closeOnClick onClick={openDuplicateModal}>
                  <FileCopyLine />
                  {t("project.header.dropdown.duplicate")}
                </Option>
              )}
              {canResetProject && (
                <Option closeOnClick onClick={openResetAnalysesModal}>
                  <RestartLine />
                  {t("projects.modal.restart_analysis_selection.title")}
                </Option>
              )}
              {canDoAdminTasks && (
                <Option
                  closeOnClick
                  onClick={() => launchCac({ routeParams: { projectId: project.id } })}
                >
                  <RefreshLine />
                  {t("projects.modal.launch_cac.title")}
                </Option>
              )}
              {canDoAdminTasks && (
                <Option
                  closeOnClick
                  onClick={() => dispatchCac({ routeParams: { projectId: project.id } })}
                >
                  <RefreshFill />
                  {t("projects.modal.dispatch_cac.title")}
                </Option>
              )}
              {canDoAdminTasks && (
                <Option
                  closeOnClick
                  onClick={() => resetCac({ routeParams: { projectId: project.id } })}
                >
                  <RefreshFill />
                  {t("projects.modal.reset_cac.title")}
                </Option>
              )}
              {canDoAdminTasks && (
                <Option
                  closeOnClick
                  onClick={async () => {
                    const { body } = await createMissingAnalysesCac({
                      routeParams: { projectId: project.id },
                    });
                    addToast("success", body, "Result");
                  }}
                >
                  <AddCircleLine />
                  {t("projects.modal.create_missing_analyses_cac.title")}
                </Option>
              )}
            </OptionGroup>
            {canManageProject && (
              <OptionGroup>
                <Option
                  closeOnClick
                  title={
                    isProdProject ? t("projects.modal.delete.prod.project.tooltip") : undefined
                  }
                  onClick={isProdProject ? undefined : openDeleteModal}
                  disabled={isProdProject}
                >
                  <DeleteBinLine />
                  {t("project.header.dropdown.delete")}
                </Option>
              </OptionGroup>
            )}
          </>
        }
      >
        <ButtonIconV2 theme="dark" data-cy="header-dropdown-menu" className={HIDE_ON_EXPORT}>
          <MoreFill size="m" />
        </ButtonIconV2>
      </StyledMenu>
    </>
  );
}

export default HeaderDropdownMenu;
